<template>
  <v-card>
    <v-card-title class="media-title">
      <div class="mr-2">{{ $t('settings.media.title') }}</div>
      &nbsp;
      <cp-help i18n="media"/>
    </v-card-title>
    <v-card-text v-if="supported" class="pr-8 media-select">
      <v-select class="media-dev-in"
        prepend-icon="mdi-microphone-settings"
        v-model="devInput"
        :items="input"
        item-text="label"
        item-value="id"
        :label="$t('settings.media.labelInput')"
        :loading="loading"
      ></v-select>
      <v-select class="media-dev-out"
        prepend-icon="mdi-volume-source"
        v-model="devOutput"
        :items="output"
        item-text="label"
        item-value="id"
        :label="$t('settings.media.labelOutput')"
        :loading="loading"
      ></v-select>
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="flex: 1 1 0; display: flex; flex-direction: column; gap: 0.5em;">
          <v-select
            prepend-icon="mdi-music-note"
            v-model="ringingTone"
            :items="ringtones"
            :label="$t('settings.media.labelRingtone')"
          />
          <v-slider class="media-dev-volume"
            v-model="ringingVolume"
            :min="0"
            :max="1"
            :step="0.1"
            :label="$t('settings.media.labelRingingVolume')"
            thumb-label="always"
            ticks
            tick-size="4"
            prepend-icon="mdi-volume-high"
          >
            <template v-slot:thumb-label>
              <span>{{ Math.round(ringingVolume * 10) }}</span>
            </template>
          </v-slider>
        </div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small :loading="playing" @click="testRinging" v-on="on" style="align-self: center;">
              <v-icon color="green">mdi-play</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('settings.media.ringBtnTooltip') }}</span>
        </v-tooltip>
      </div>
      <v-switch class="ringback-dev-switch" v-model="ringbackEnable"
        :label="$t('settings.media.enableRingDev')">
        <template v-slot:prepend>
          <v-icon :color="ringbackEnable ? 'green' : 'grey'">
            {{ ringbackEnable ? 'mdi-cast-audio' : 'mdi-speaker-off' }}
          </v-icon>
        </template>
      </v-switch>
      <v-select class="media-dev-ringback"
        prepend-icon="mdi-usb-flash-drive"
        v-model="devRinger"
        :disabled="!ringbackEnable"
        :items="output"
        item-text="label"
        item-value="id"
        :label="$t('settings.media.labelRingDev')"
        :loading="loading">
        <template v-slot:append-outer>
          <cp-help i18n="mediaRingDev"/>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn :disabled="!ringbackEnable && ringbackFullyLoaded" :loading="playing" icon small v-on="on" @click="testRinging">
                <v-icon color="green">mdi-play</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('settings.media.testBtnTooltip') }}</span>
          </v-tooltip>
        </template>
      </v-select>
    </v-card-text>
    <v-card-text v-else class="media-unsupported">
      <v-alert color="orange" type="error" prominent class="text-justify">
        {{ $t('settings.media.notSupported') }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import LocalStorage from 'Store/class/local_storage'
import BrowserMedia from 'Store/sipua/browser_media'
import Sounds from '@/sipua/sounds'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'settings-media-device',
  data () {
    return {
      supported: false,
      playing: false,
      audio: new Sounds()
    }
  },
  mounted () {
    this.supported = BrowserMedia.HasSupport()
  },
  computed: {
    ...mapGetters({
      browserMedia: 'sipua/browserMedia',
      userID: 'settings/userID'
    }),
    loading: function () {
      return this.browserMedia.loading
    },
    ringbackFullyLoaded () {
      return this.audio.canPlayAudio
    },
    ringbackEnable: {
      get: function () {
        return this.browserMedia.rbEnabled
      },
      set: function (val) {
        this.setRingerDevEnabled(val)
      }
    },
    input: function () {
      return this.browserMedia.input
    },
    output: function () {
      return this.browserMedia.output
    },
    devInput: {
      get: function () {
        return LocalStorage.matchInputDevice(this.input)
      },
      set: function (devId) {
        this.setInputDevice(devId)
        LocalStorage.setDeviceInput(devId)
      }
    },
    devOutput: {
      get: function () {
        return LocalStorage.matchOutputDevice(this.output)
      },
      set: function (devId) {
        this.stopRinging()
        this.setOutputDevice(devId)
        LocalStorage.setDeviceOutput(devId)
      }
    },
    devRinger: {
      get: function () {
        return LocalStorage.matchRingerDevice(this.output)
      },
      set: function (devId) {
        this.stopRinging()
        this.setRingerDevice(devId)
        LocalStorage.setDeviceRinger(devId) // should be relabeled to 'setRingerDevice'
      }
    },
    ringtones: function () {
      return this.audio.ringtones()
    },
    ringingTone: {
      get: function () {
        return this.browserMedia.ringingTone
      },
      set: async function (tone) {
        this.stopRinging()
        await this.setRingingTone({ user_id: this.userID, tone: tone })
        this.audio = new Sounds(tone)
      }
    },
    ringingVolume: {
      get: function () {
        return this.browserMedia.ringingVolume
      },
      set: function (volume) {
        this.setRingingVolume(volume)
      }
    }
  },
  methods: {
    ...mapActions({
      setInputDevice: 'sipua/setInputDevice',
      setOutputDevice: 'sipua/setOutputDevice',
      setRingerDevice: 'sipua/setRingerDevice',
      setRingerDevEnabled: 'sipua/setRingerDevEnabled',
      setRingingTone: 'sipua/setRingingTone',
      setRingingVolume: 'sipua/setRingingVolume'
    }),
    testRinging: function () {
      const dev = LocalStorage.matchRingerDevice(this.output)
      this.dbg(`Testing ringing output device. Selected device '${dev.label}', id: ${dev.id}`)
      this.playing = true
      this.audio.startRinging(dev.id)
      setTimeout(() => {
        this.stopRinging()
      }, 3200)
    },
    stopRinging: function () {
      this.playing = false
      this.audio.stopRinging()
    }
  }
}
</script>

<style scoped>
</style>
